// apps/api/src/qxcircle/qxcircle-block.ts
// Description: QXCircle block author visibility · blocks list (Slice 16).
// Owner: Masa Bayu
// Date: 2026-08-24

import type {
  QxCircleBlocksResponse,
  QxCircleBlockResult,
  QxCircleFeedPost,
} from "./qxcircle.types";
import { findProfileSeed } from "./qxcircle-profile-seeds";
import { normalizeQxCircleAuthorHandle } from "./qxcircle-service-helpers";
import { BadRequestException, NotFoundException } from "./_nestjs-common";

export function isQxCirclePostVisible(
  post: QxCircleFeedPost,
  hiddenPostIds: Set<string>,
  blockedHandles: Set<string>,
): boolean {
  if (hiddenPostIds.has(post.id)) return false;
  if (blockedHandles.has(post.authorHandle)) return false;
  return true;
}

export function filterQxCircleVisiblePosts(
  posts: QxCircleFeedPost[],
  hiddenPostIds: Set<string>,
  blockedHandles: Set<string>,
): QxCircleFeedPost[] {
  return posts.filter((post) =>
    isQxCirclePostVisible(post, hiddenPostIds, blockedHandles),
  );
}

export function isQxCircleAuthorBlocked(
  handleRaw: string,
  blockedHandles: Set<string>,
): boolean {
  const authorHandle = normalizeQxCircleAuthorHandle(handleRaw);
  return blockedHandles.has(authorHandle);
}

export function buildQxCircleBlocksList(
  posts: QxCircleFeedPost[],
  blockedHandles: Set<string>,
): QxCircleBlocksResponse {
  const items = [...blockedHandles]
    .map((authorHandle) => {
      const post = posts.find((p) => p.authorHandle === authorHandle);
      const seed = findProfileSeed(authorHandle);
      return {
        authorHandle,
        authorName: post?.authorName ?? seed?.authorName ?? authorHandle,
      };
    })
    .sort((a, b) => a.authorHandle.localeCompare(b.authorHandle));
  return {
    world: "qxcircle",
    slice: "block",
    count: items.length,
    items,
  };
}

export function toggleQxCircleBlockAuthor(
  handleRaw: string,
  posts: QxCircleFeedPost[],
  localBlocks: Set<string>,
  localFollows: Set<string>,
  viewerHandle: string | null = null,
): QxCircleBlockResult {
  const authorHandle = normalizeQxCircleAuthorHandle(handleRaw);
  if (viewerHandle && authorHandle === viewerHandle) {
    throw new BadRequestException("Cannot block yourself");
  }
  const authorPost = posts.find((p) => p.authorHandle === authorHandle);
  if (!authorPost) {
    throw new NotFoundException(`Author not found: ${authorHandle}`);
  }
  if (localBlocks.has(authorHandle)) {
    localBlocks.delete(authorHandle);
  } else {
    localBlocks.add(authorHandle);
    localFollows.delete(authorHandle);
  }
  const blockedByLocal = localBlocks.has(authorHandle);
  return {
    authorHandle,
    authorName: authorPost.authorName,
    blockedByLocal,
    note: blockedByLocal
      ? "Author blocked locally — posts hidden from feed (lab simulate, not prod moderation)"
      : "Author unblocked",
  };
}
